/**
 * Scan POS P6 -- the compact Mobile Scanner control beside the barcode box.
 *
 *   PASSIVE tab        "🟡 Mobile Scanner ใช้งานอยู่ในหน้าขายอื่น" + explicit "ย้ายมาหน้านี้"
 *   ACTIVE, recovering "🟡 Mobile Scanner กำลังเชื่อมต่อ"
 *   ACTIVE, no pair    "📱 เชื่อมมือถือ"  -> one 60 s QR (no auto-refresh)
 *   ACTIVE, paired     "🟢 📱 <scanner>"  -> panel: details, replace, disconnect
 *
 * The QR holds ONLY the Backend's `invitation.qr_payload`, rendered locally
 * (antd QRCode, no network service). The payload lives in component state
 * only while its modal shows it: never logged, never stored, never put in a
 * URL. Replacing the scanner is explicit and version-checked; a stale
 * version refreshes the view and is NOT retried.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal, QRCode, message } from 'antd';
import {
    Invitation, PairStatus, WebPairView, pairDisconnectWeb, pairInvitationCreate, pairReplace, pairStatusWeb,
    scanErrorCode,
} from '../../api/scan-pos';
import { SurfaceSnapshot } from '../../scan-pos/surface-session';

export const PAIR_POLL_MS = 2_000;
export const PAIR_REFRESH_MS = 30_000;

interface Props {
    snapshot: SurfaceSnapshot;
    onTransfer(): Promise<boolean>;
    onRetry(): Promise<void>;
    /**
     * A pairing dialog is about to cover the Cart (true) / has closed (false).
     * Called BEFORE it opens, so the page can PAUSE the receiver first.
     */
    onOverlay?(open: boolean): void;
}

interface QrState {
    payload: string;
    terminalName: string;
    expiresAt: number;           // local clock: received + expires_in_seconds
    purpose: string;
}

const formatTime = (iso: string | null) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString('th-TH');
};

const pairLabel = (pair: WebPairView) => pair.mobile_employee_name || pair.mobile_device_label || 'มือถือ';

const ScannerControl: React.FC<Props> = ({ snapshot, onTransfer, onRetry, onOverlay }) => {
    const [status, setStatus] = useState<PairStatus | null>(null);
    const [qr, setQr] = useState<QrState | null>(null);
    const [qrExpired, setQrExpired] = useState(false);
    const [now, setNow] = useState(Date.now());
    const [panelOpen, setPanelOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const pairAtQr = useRef<number | null>(null);
    const active = snapshot.role === 'ACTIVE';

    const refresh = useCallback(async (): Promise<PairStatus | null> => {
        try {
            const next = await pairStatusWeb();
            setStatus(next);
            return next;
        } catch {
            return null;
        }
    }, []);

    // Pair status for the ACTIVE tab: on becoming active, then every 30 s.
    useEffect(() => {
        if (!active) return undefined;
        void refresh();
        const timer = setInterval(() => { void refresh(); }, PAIR_REFRESH_MS);
        return () => clearInterval(timer);
    }, [active, refresh]);

    const closeQr = useCallback(() => {
        setQr(null);                    // the raw payload leaves memory with the modal
        setQrExpired(false);
        pairAtQr.current = null;
        onOverlay?.(false);
    }, [onOverlay]);

    const openPanel = () => {
        onOverlay?.(true);
        setPanelOpen(true);
    };

    const closePanel = () => {
        setPanelOpen(false);
        onOverlay?.(false);
    };

    const showQr = useCallback((invitation: Invitation, terminalName: string) => {
        onOverlay?.(true);
        setQrExpired(false);
        setQr({
            payload: invitation.qr_payload,
            terminalName,
            expiresAt: Date.now() + (Number(invitation.expires_in_seconds) || 60) * 1000,
            purpose: invitation.purpose,
        });
    }, [onOverlay]);

    // While a QR is shown: countdown, expiry (no refresh), and bounded polling
    // for the redemption.
    useEffect(() => {
        if (!qr) return undefined;
        let handled = false;                 // one success, even if two polls overlap
        const tick = setInterval(() => {
            const t = Date.now();
            setNow(t);
            if (t >= qr.expiresAt) {
                setQrExpired(true);
                setQr((current) => (current ? { ...current, payload: '' } : current));
            }
        }, 500);
        const poll = setInterval(async () => {
            if (handled || Date.now() >= qr.expiresAt) return;
            const next = await refresh();
            if (handled) return;
            if (next?.pair && next.pair.pair_id !== pairAtQr.current) {
                handled = true;
                closeQr();
                message.success(`📱 เชื่อมต่อ ${pairLabel(next.pair)} แล้ว`);
            }
        }, PAIR_POLL_MS);
        return () => { handled = true; clearInterval(tick); clearInterval(poll); };
    }, [qr?.expiresAt, refresh, closeQr]); // eslint-disable-line react-hooks/exhaustive-deps

    const connect = async () => {
        setBusy(true);
        try {
            const res = await pairInvitationCreate();
            setStatus({ terminal: res.terminal, version: res.version, pair: res.pair });
            pairAtQr.current = res.pair?.pair_id ?? null;
            showQr(res.invitation, res.terminal?.name || '');
        } catch (error) {
            message.error(scanErrorCode(error) === 'TERMINAL_NOT_REGISTERED'
                ? 'เครื่องนี้ยังไม่ได้ลงทะเบียนเป็นเครื่อง POS'
                : 'สร้าง QR สำหรับเชื่อมมือถือไม่สำเร็จ');
        } finally {
            setBusy(false);
        }
    };

    const disconnect = () => {
        const pair = status?.pair;
        if (!pair) return;
        Modal.confirm({
            title: 'ยกเลิกการเชื่อมต่อมือถือ',
            content: `ยกเลิกการเชื่อมต่อกับ ${pairLabel(pair)} ใช่หรือไม่ (บิลปัจจุบันไม่ถูกเปลี่ยน)`,
            okText: 'ยกเลิกการเชื่อมต่อ', cancelText: 'ไม่', centered: true,
            onOk: async () => {
                try {
                    await pairDisconnectWeb(pair.pair_id);
                    message.success('ยกเลิกการเชื่อมต่อแล้ว');
                } catch (error) {
                    message.warning(scanErrorCode(error) === 'PAIR_NOT_CURRENT'
                        ? 'สถานะเปลี่ยนไปแล้ว แสดงสถานะล่าสุดแทน' : 'ยกเลิกการเชื่อมต่อไม่สำเร็จ');
                }
                closePanel();
                await refresh();
            },
        });
    };

    const replace = () => {
        Modal.confirm({
            title: 'เปลี่ยนเครื่องสแกน',
            content: 'มือถือเครื่องเดิมจะถูกตัดการเชื่อมต่อ แล้วสร้าง QR ใหม่ให้มือถือเครื่องใหม่สแกน (บิลปัจจุบันไม่ถูกเปลี่ยน)',
            okText: 'เปลี่ยนเครื่อง', cancelText: 'ไม่', centered: true,
            onOk: async () => {
                const current = await refresh();                 // the exact pair + version on screen NOW
                if (!current?.pair) {
                    message.info('ไม่มีมือถือเชื่อมต่ออยู่แล้ว');
                    return;
                }
                try {
                    const res = await pairReplace(current.pair.pair_id, current.version);
                    setPanelOpen(false);                          // the QR overlay takes over
                    pairAtQr.current = null;
                    setStatus({ ...current, version: res.version, pair: null });
                    showQr(res.invitation, current.terminal?.name || '');
                } catch (error) {
                    // STALE_VERSION / PAIR_NOT_CURRENT: show what is true now, do not retry.
                    await refresh();
                    message.warning(['STALE_VERSION', 'PAIR_NOT_CURRENT'].includes(scanErrorCode(error) || '')
                        ? 'สถานะการจับคู่เปลี่ยนไปแล้ว กรุณาตรวจสอบเครื่องสแกนปัจจุบัน'
                        : 'เปลี่ยนเครื่องสแกนไม่สำเร็จ');
                }
            },
        });
    };

    const transfer = () => {
        Modal.confirm({
            title: 'ย้าย Mobile Scanner มาหน้านี้',
            content: 'หน้าขายอื่นจะหยุดรับการสแกน และหน้านี้จะรับแทน',
            okText: 'ย้ายมาหน้านี้', cancelText: 'ไม่', centered: true,
            onOk: async () => {
                const ok = await onTransfer();
                if (!ok) message.warning('ย้ายไม่สำเร็จ สถานะหน้าขายเปลี่ยนไปแล้ว กรุณาลองใหม่');
            },
        });
    };

    // ------------------------------------------------------------ render
    let control: React.ReactNode;
    if (snapshot.role === 'PASSIVE') {
        control = (
            <span className='scanner-status scanner-status--passive' data-testid='scanner-passive'>
                🟡 Mobile Scanner ใช้งานอยู่ในหน้าขายอื่น
                <Button size='small' type='link' onClick={transfer} data-testid='scanner-transfer'>ย้ายมาหน้านี้</Button>
            </span>
        );
    } else if (!active) {
        control = <span className='scanner-status' data-testid='scanner-unavailable'>Mobile Scanner ไม่พร้อม</span>;
    } else if (snapshot.channel !== 'VERIFIED') {
        control = (
            <span className='scanner-status scanner-status--recovering' data-testid='scanner-recovering'>
                🟡 Mobile Scanner กำลังเชื่อมต่อ
                {snapshot.channel === 'FAILED' &&
                    <Button size='small' type='link' onClick={() => { void onRetry(); }}>ลองใหม่</Button>}
            </span>
        );
    } else if (status?.pair) {
        control = (
            <Button size='small' className='scanner-status scanner-status--paired' onClick={openPanel}
                data-testid='scanner-paired'>
                🟢 📱 {pairLabel(status.pair)}
            </Button>
        );
    } else {
        control = (
            <Button size='small' className='scanner-connect' onClick={connect} loading={busy} data-testid='scanner-connect'>
                📱 เชื่อมมือถือ
            </Button>
        );
    }

    const secondsLeft = qr ? Math.max(0, Math.ceil((qr.expiresAt - now) / 1000)) : 0;

    return (
        <div className='scanner-control' data-testid='scanner-control'>
            {control}

            <Modal open={!!qr} centered footer={null} width={380} destroyOnClose onCancel={closeQr}
                title='📱 เชื่อมมือถือเป็นเครื่องสแกน'>
                {qr && (
                    <div className='scanner-qr' data-testid='scanner-qr'>
                        <div className='scanner-qr__terminal'>เครื่อง POS: <b>{qr.terminalName || '-'}</b></div>
                        {!qrExpired && qr.payload ? (
                            <>
                                <QRCode value={qr.payload} size={220} bordered={false} />
                                <div className='scanner-qr__countdown' data-testid='scanner-qr-countdown'>
                                    สแกนด้วยแอป POS Mobile ภายใน {secondsLeft} วินาที
                                </div>
                            </>
                        ) : (
                            <div className='scanner-qr__expired' data-testid='scanner-qr-expired'>
                                QR หมดอายุแล้ว
                                <div>
                                    <Button onClick={() => { closeQr(); void connect(); }} data-testid='scanner-qr-new'>
                                        สร้าง QR ใหม่
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            <Modal open={panelOpen && !!status?.pair} centered width={420} title='📱 เครื่องสแกน'
                onCancel={closePanel}
                footer={[
                    <Button key='replace' onClick={replace} data-testid='scanner-replace'>เปลี่ยนเครื่องสแกน</Button>,
                    <Button key='disconnect' danger onClick={disconnect} data-testid='scanner-disconnect'>ยกเลิกการเชื่อมต่อ</Button>,
                ]}>
                {status?.pair && (
                    <div className='scanner-panel' data-testid='scanner-panel'>
                        <div>สถานะ: เชื่อมต่ออยู่</div>
                        <div>พนักงานที่สแกน: {status.pair.mobile_employee_name || '-'}</div>
                        <div>อุปกรณ์: {status.pair.mobile_device_label || '-'}</div>
                        <div>เครื่อง POS: {status.terminal?.name || '-'}</div>
                        <div>เชื่อมต่อตั้งแต่: {formatTime(status.pair.created_at)}</div>
                        <div>หมดอายุ: {formatTime(status.pair.expires_at)}</div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default ScannerControl;
